import { API_BASE_URL, API_TIMEOUT } from "../config/api";
import {
  getAuthToken,
  handleSessionExpired,
  isTokenExpired,
} from "../utils/auth";
import { parseApiErrorMessage } from "../utils/errors";

const API_DEBUG = import.meta.env.VITE_API_DEBUG === "true";
const debugLog = (...args: unknown[]) => {
  if (API_DEBUG) console.log(...args);
};

/**
 * API Response Format
 * Standard response format from our NestJS backend
 */
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  message?: string;
}

/**
 * API Error
 * Custom error class for API errors
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public data?: any,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * API Client Options
 */
interface ApiClientOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  body?: any;
  headers?: Record<string, string>;
  params?: Record<string, any>;
  timeout?: number;
}

/**
 * Build query string from params object
 *
 * @param params - Query parameters
 * @returns Query string
 */
const buildQueryString = (params: Record<string, any>): string => {
  if (!params || Object.keys(params).length === 0) {
    return "";
  }

  const query: Record<string, string> = {};

  Object.keys(params).forEach((key) => {
    if (
      params[key] !== undefined &&
      params[key] !== null &&
      params[key] !== ""
    ) {
      query[key] = String(params[key]);
    }
  });

  const queryString = new URLSearchParams(query).toString();
  return queryString ? `?${queryString}` : "";
};

/**
 * API Client
 * Handles all HTTP requests to the backend API
 * Includes authentication, error handling, and timeout support
 *
 * @param endpoint - API endpoint (e.g., "/users" or "/business/123")
 * @param options - Request options (method, body, headers, params, timeout)
 * @returns Promise with response data
 * @throws ApiError if request fails
 */
export const apiClient = async <T = any>(
  endpoint: string,
  options: ApiClientOptions = {},
): Promise<T> => {
  const {
    method = "GET",
    body,
    headers: customHeaders = {},
    params = {},
    timeout = API_TIMEOUT,
  } = options;

  // Build full URL with query params
  const queryString = buildQueryString(params);

  // Ensure we don't have double slashes if endpoint starts with one
  const cleanEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;

  // Combine base URL, endpoint and query string
  // If endpoint already has a ?, use & for the queryString (but buildQueryString adds ?)
  let finalQueryString = queryString;
  if (queryString && cleanEndpoint.includes("?")) {
    finalQueryString = queryString.replace("?", "&");
  }

  const url = `${API_BASE_URL}${cleanEndpoint}${finalQueryString}`;

  // Get authentication token from localStorage
  const token = getAuthToken();

  if (token && isTokenExpired(token)) {
    handleSessionExpired();
    throw new ApiError("Session expired. Please login again.", 401);
  }

  // Build headers
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...customHeaders,
  };

  // Add authorization header if token exists
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Build request options
  const requestOptions: RequestInit = {
    method,
    headers,
  };

  // Add body for POST, PUT, PATCH requests
  if (body && ["POST", "PUT", "PATCH"].includes(method)) {
    requestOptions.body = JSON.stringify(body);
  }

  try {
    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    // Make request
    const response = await fetch(url, {
      ...requestOptions,
      signal: controller.signal,
    });

    // Clear timeout
    clearTimeout(timeoutId);

    // Parse response
    let data: any;
    const contentType = response.headers.get("content-type");

    if (contentType && contentType.includes("application/json")) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    // Handle error responses
    if (!response.ok) {
      // Handle 401 Unauthorized - clear auth and redirect
      if (response.status === 401) {
        handleSessionExpired(
          data?.message || "Session expired. Please login again.",
        );
      }

      // Surface 403s as a toast via a custom event so callers don't each have to
      // handle it. The toast listener lives in App.tsx.
      if (response.status === 403) {
        try {
          window.dispatchEvent(
            new CustomEvent("rbac:forbidden", {
              detail: {
                message:
                  data?.message || "You don't have permission to do that.",
              },
            }),
          );
        } catch {
          /* SSR safety */
        }
      }

      throw new ApiError(
        parseApiErrorMessage(data, response.status),
        response.status,
        data,
      );
    }

    debugLog(`[API] ${method} ${endpoint}`, {
      status: response.status,
      url,
      response: data,
    });

    // Our backend returns { success: true, data: {...}, pagination?: {...} }
    if (
      data &&
      typeof data === "object" &&
      "success" in data &&
      "data" in data
    ) {
      if ("pagination" in data) {
        debugLog("[API] Pagination response detected:", {
          dataCount: data.data?.length,
          pagination: data.pagination,
        });
        return { data: data.data, pagination: data.pagination } as T;
      }
      debugLog("[API] Standard response, extracting data field");
      return data.data as T;
    }

    debugLog("[API] Non-standard response format, returning as-is");
    return data as T;
  } catch (error: any) {
    // Handle timeout error
    if (error.name === "AbortError") {
      throw new ApiError("Request timeout", 408);
    }

    // Handle network errors
    if (error instanceof TypeError && error.message === "Failed to fetch") {
      throw new ApiError("Network error. Please check your connection.", 0);
    }

    // Re-throw ApiError
    if (error instanceof ApiError) {
      throw error;
    }

    // Handle other errors
    throw new ApiError(error.message || "An unexpected error occurred");
  }
};

/**
 * Helper functions for common HTTP methods
 */

/**
 * GET request
 */
export const get = <T = any>(
  endpoint: string,
  params?: Record<string, any>,
): Promise<T> => {
  return apiClient<T>(endpoint, { method: "GET", params });
};

/**
 * POST request
 */
export const post = <T = any>(endpoint: string, body: any): Promise<T> => {
  return apiClient<T>(endpoint, { method: "POST", body });
};

/**
 * PUT request
 */
export const put = <T = any>(endpoint: string, body: any): Promise<T> => {
  return apiClient<T>(endpoint, { method: "PUT", body });
};

/**
 * PATCH request
 */
export const patch = <T = any>(endpoint: string, body: any): Promise<T> => {
  return apiClient<T>(endpoint, { method: "PATCH", body });
};

/**
 * DELETE request
 */
export const del = <T = any>(endpoint: string): Promise<T> => {
  return apiClient<T>(endpoint, { method: "DELETE" });
};

/**
 * POST multipart/form-data — lets the browser set the boundary.
 */
export const postForm = async <T = any>(
  endpoint: string,
  form: FormData,
): Promise<T> => {
  const cleanEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  const url = `${API_BASE_URL}${cleanEndpoint}`;
  const token = getAuthToken();
  if (token && isTokenExpired(token)) {
    handleSessionExpired();
    throw new ApiError("Session expired. Please login again.", 401);
  }
  const headers: Record<string, string> = { Accept: "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(url, { method: "POST", headers, body: form });
  const contentType = res.headers.get("content-type") ?? "";
  const data: any = contentType.includes("application/json")
    ? await res.json()
    : await res.text();
  if (!res.ok) {
    if (res.status === 401) handleSessionExpired(data?.message);
    throw new ApiError(
      parseApiErrorMessage(data, res.status),
      res.status,
      data,
    );
  }
  if (data && typeof data === "object" && "success" in data && "data" in data) {
    return data.data as T;
  }
  return data as T;
};
